import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { FEATURES, type Feature } from '@/types/settings'

export interface ExperimentalFeature {
  id: string
  name: string
  description: string
  warningLevel: 'warning' | 'critical'
  risks: string[]
  academicPapers?: string[]
}

interface ExperimentalState {
  acceptedFeatures: Set<string>
  showWarningModal: boolean
  pendingFeature: ExperimentalFeature | null
  
  // Actions
  checkFeatureAcceptance: (featureId: string) => boolean
  requestFeatureAccess: (featureId: string) => void
  acceptFeatureRisks: (featureId: string) => void
  dismissWarning: () => void
  resetAcceptedFeatures: () => void
}

export const useExperimentalStore = create<ExperimentalState>()(
  persist(
    (set, get) => ({
      acceptedFeatures: new Set<string>(),
      showWarningModal: false,
      pendingFeature: null,
      
      checkFeatureAcceptance: (featureId: string) => {
        return get().acceptedFeatures.has(featureId)
      },
      
      requestFeatureAccess: (featureId: string) => {
        const feature = FEATURES[featureId]
        if (!feature || !feature.requiresConsent) {
          return
        }
        
        // Check if already accepted
        if (get().acceptedFeatures.has(featureId)) {
          return
        }
        
        // Convert Feature to ExperimentalFeature format
        const experimentalFeature: ExperimentalFeature = {
          id: featureId,
          name: feature.name,
          description: feature.technicalDetails || feature.description,
          warningLevel: feature.maturity === 'alpha' ? 'critical' : 'warning',
          risks: [
            'This feature is experimental and has not been audited',
            'Using this feature may result in loss of funds',
            'The cryptographic implementations may contain vulnerabilities',
            feature.maturity === 'alpha' 
              ? 'This is ALPHA software - expect bugs and breaking changes'
              : 'This is BETA software - use with caution'
          ],
          academicPapers: feature.academicReferences
        }
        
        // Add specific risks for certain features
        if (featureId === 'DILITHIUM_RKS') {
          experimentalFeature.risks.push(
            'Non-hardened derivation for quantum keys is theoretical',
            'Key rerandomization may weaken security properties',
            'Watch-only wallets with quantum keys are unproven'
          )
        }
        
        set({
          showWarningModal: true,
          pendingFeature: experimentalFeature
        })
      },
      
      acceptFeatureRisks: (featureId: string) => {
        set((state) => ({
          acceptedFeatures: new Set([...state.acceptedFeatures, featureId]),
          showWarningModal: false,
          pendingFeature: null
        }))
      },
      
      dismissWarning: () => {
        set({
          showWarningModal: false,
          pendingFeature: null
        })
      },
      
      resetAcceptedFeatures: () => {
        set({
          acceptedFeatures: new Set<string>(),
          showWarningModal: false,
          pendingFeature: null
        })
      }
    }),
    {
      name: 'experimental-features',
      partialize: (state) => ({
        acceptedFeatures: Array.from(state.acceptedFeatures)
      }),
      merge: (persisted: any, current) => ({
        ...current,
        acceptedFeatures: new Set(persisted?.acceptedFeatures || [])
      })
    }
  )
)